import React, { useId, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, ChevronDown } from 'lucide-react';
import {
  COMMITMENT_MONTHS,
  DAILY_PRICE_CEIL,
  FIRST_YEAR_TOTAL,
  LOCAL_ZONE_TEXT,
  MAX_PAGES,
  MONTHLY_INCLUDES,
  MONTHLY_PRICE,
  SETUP_INCLUDES,
  SETUP_PRICE,
  VAT_MENTION,
  formatEuro,
} from './offerData';
import { OfferFormTextLink, OfferWhatsAppCta } from './OfferCtaButtons';

interface PriceColumnProps {
  title: string;
  amount: string;
  suffix: string;
  note: string;
  items: readonly string[];
  accentClass: string;
}

const PriceColumn: React.FC<PriceColumnProps> = ({ title, amount, suffix, note, items, accentClass }) => (
  <div className="rounded-xl border border-white/15 bg-white/5 p-5 md:p-6">
    <p className="text-xs uppercase tracking-wider text-[#AEB9FF] mb-2">{title}</p>
    <p className="text-white">
      <span className="text-4xl md:text-5xl font-bold">{amount}</span>
      <span className="ml-2 text-sm text-gray-300">{suffix}</span>
    </p>
    <p className="text-xs text-[#B9C4FF] mt-1 mb-5">{note}</p>
    <ul className="text-gray-300 text-sm space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${accentClass}`} /> {item}
        </li>
      ))}
    </ul>
  </div>
);

const CommitmentExplainer: React.FC = () => {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return (
    <div className="mt-3">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm text-[#9BA8FF] hover:text-white transition-colors"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((value) => !value)}
      >
        Pourquoi un engagement de {COMMITMENT_MONTHS} mois ?
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            id={contentId}
            key="commitment"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden text-gray-300 text-sm leading-relaxed max-w-xl mx-auto mt-2"
          >
            Il nous permet de proposer la création à {formatEuro(SETUP_PRICE)} au lieu de plusieurs milliers d'euros : le coût est étalé sur la première année. Ensuite, vous êtes libre chaque mois.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

const OfferPricing: React.FC = () => (
  <section id="web-vitrine" className="py-16 bg-[#2F3136]">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center mb-10"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <h2 className="text-balance text-2xl md:text-3xl font-bold text-white mb-3">
          Site internet pour <span className="text-[#5865F2]">micro-entrepreneurs</span>, artisans et TPE : jusqu'à {MAX_PAGES} pages
        </h2>
        <p className="text-gray-300 max-w-2xl mx-auto mb-2">
          Une seule offre, un prix clair, et tout ce qu'il faut pour être visible et crédible en ligne.
        </p>
        <p className="text-gray-400 text-sm max-w-2xl mx-auto">{LOCAL_ZONE_TEXT}</p>
      </motion.div>

      <motion.div
        className="rounded-2xl border border-[#5865F2]/40 bg-gradient-to-b from-white/10 to-white/[0.03] p-5 md:p-8 shadow-xl shadow-[#5865F2]/10"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          <PriceColumn
            title="À la création"
            amount={formatEuro(SETUP_PRICE)}
            suffix="à la mise en ligne"
            note="Aucun acompte"
            items={SETUP_INCLUDES}
            accentClass="text-[#5865F2]"
          />
          <PriceColumn
            title="Chaque mois"
            amount={formatEuro(MONTHLY_PRICE)}
            suffix="par mois"
            note={`Soit moins de ${DAILY_PRICE_CEIL}\u00a0€ par jour`}
            items={MONTHLY_INCLUDES}
            accentClass="text-cyan-400"
          />
        </div>

        <div className="text-center">
          <p className="text-white font-semibold text-lg">{formatEuro(FIRST_YEAR_TOTAL)} la première année</p>
          <p className="text-gray-400 text-sm mt-1">
            Engagement {COMMITMENT_MONTHS} mois, puis résiliable chaque mois · Hébergement et nom de domaine à votre nom, non inclus (quelques euros par mois) · {VAT_MENTION}
          </p>
          <CommitmentExplainer />
          <div className="mt-6 flex flex-col items-center gap-3">
            <OfferWhatsAppCta location="pricing" />
            <OfferFormTextLink location="pricing" />
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default OfferPricing;
